import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { VEHICLE_TYPES, GAME_DEFAULTS } from '../lib/constants.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { saveSession } from '../lib/session.js'
import { supabase } from '../lib/supabase.js'
import { pageBg as podiumBg } from '../lib/images'
import VehiclePicker from '../components/ui/VehiclePicker.jsx'

const DIFFICULTY_COLOR = {
  'Simple':        'bg-blue-500/20 text-blue-300',
  'Intermédiaire': 'bg-amber-500/20 text-amber-300',
  'Difficile':     'bg-red-500/20 text-red-300',
}

// Modal de confirmation pour retirer un quiz
function ConfirmRemoveModal({ deckTitle, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-gray-900 border border-white/15 rounded-2xl p-6 max-w-sm w-full space-y-4">
        <h3 className="text-white font-semibold text-base">Retirer ce quiz ?</h3>
        <p className="text-white/70 text-sm">
          Souhaites-tu enlever <strong className="text-white">"{deckTitle}"</strong> de tes quiz partagés ?
        </p>
        <div className="flex gap-3 justify-end pt-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-white/60 hover:text-white text-sm transition rounded-lg hover:bg-white/10"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium rounded-lg transition"
          >
            Oui, retirer
          </button>
        </div>
      </div>
    </div>
  )
}

// Modal pour lancer le solo (pseudo + véhicule) — overlay plein écran style JoinPage
function PlaySoloModal({ deck, session, onCancel }) {
  const navigate = useNavigate()
  const firstName = session?.user?.user_metadata?.full_name?.split(' ')[0]
    ?? session?.user?.email?.split('@')[0]
    ?? 'Joueur'
  const savedVehicle = localStorage.getItem('solo_vehicle_type')
  const [vehicle, setVehicle] = useState(
    VEHICLE_TYPES.find(v => v.emoji === savedVehicle)?.emoji ?? VEHICLE_TYPES[0].emoji
  )
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  async function handlePlay() {
    setCreating(true)
    setError('')
    try {
      const data = await apiFetch('/api/games/solo', {
        method: 'POST',
        body: JSON.stringify({
          deck_id: deck.id,
          pseudo: firstName,
          type_vehicule: vehicle,
          config: {
            tours: 1,
            timer_max: deck.timer_max ?? GAME_DEFAULTS.TIMER_MAX,
            timer_min: deck.timer_min ?? GAME_DEFAULTS.TIMER_MIN,
            vitesse_active: true,
            palier_v2: 2,
            palier_v3: 5,
          },
        }),
      })
      localStorage.setItem('solo_vehicle_type', vehicle)
      saveSession(data.code, data.session_token, data.kart.id)
      navigate(`/solo/${data.code}`)
    } catch (e) {
      setError(e.message)
      setCreating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 border border-white/15 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm flex flex-col h-[90dvh] sm:h-[88dvh]">

        {/* En-tête */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 shrink-0">
          <div className="min-w-0 pr-3">
            <h3 className="text-white font-semibold text-base">🏎️ Choisis ton véhicule</h3>
            <p className="text-white/50 text-xs mt-0.5 line-clamp-1">"{deck.titre}"</p>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white p-1 rounded-lg hover:bg-white/10 transition shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Véhicule sélectionné */}
        <div className="flex items-center gap-2 px-5 pb-3 shrink-0">
          <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1">
            <TwemojiImg emoji={vehicle} size={16} />
            <span className="text-white text-xs font-medium">
              {VEHICLE_TYPES.find(v => v.emoji === vehicle)?.label}
            </span>
          </div>
        </div>

        {/* Picker — prend tout l'espace restant */}
        <div className="flex-1 min-h-0 flex flex-col px-4">
          <VehiclePicker value={vehicle} onChange={setVehicle} />
        </div>

        {/* Erreur + Actions */}
        {error && (
          <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mx-5 mt-2 shrink-0">
            {error}
          </p>
        )}
        <div className="flex gap-3 px-5 py-4 border-t border-white/10 shrink-0">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-white/60 hover:text-white text-sm transition rounded-xl border border-white/10 hover:bg-white/10"
          >
            Annuler
          </button>
          <button
            onClick={handlePlay}
            disabled={creating}
            className="flex-1 px-5 py-2.5 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl transition disabled:opacity-60"
          >
            {creating ? 'Lancement…' : '🚀 Jouer !'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function SharedQuizzesPage() {
  const navigate = useNavigate()
  const [session, setSession] = useState(undefined)
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirmRemove, setConfirmRemove] = useState(null) // { deckId, deckTitle }
  const [playDeck, setPlayDeck] = useState(null) // deck object

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (session === null) navigate('/login', { replace: true })
  }, [session, navigate])

  useEffect(() => {
    apiFetch('/api/share')
      .then(data => setQuizzes(data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function handleRemove(deckId) {
    try {
      await apiFetch(`/api/share/${deckId}`, { method: 'DELETE' })
      setQuizzes(qs => qs.filter(q => q.deck_id !== deckId))
    } catch {
      // Erreur non bloquante
    }
    setConfirmRemove(null)
  }

  const bgStyle = { backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }

  if (session === undefined || session === null) return null

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden" style={bgStyle}>
      {/* Header */}
      <header className="bg-gray-900/95 backdrop-blur-sm px-4 py-3 flex items-center gap-3 shrink-0 border-b border-white/10">
        <button
          onClick={() => navigate('/home')}
          className="text-white/70 hover:text-white transition p-1 rounded-lg hover:bg-white/10 shrink-0"
          aria-label="Retour"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="font-semibold text-white">🤝 Partagés avec moi</span>
      </header>

      {/* Contenu principal */}
      <main className="flex-1 overflow-y-auto">
      <div className="min-h-full flex flex-col justify-center">
      <div className="max-w-3xl mx-auto w-full px-4 py-8">

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8">
              <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <p className="text-white/70 text-sm">Chargement…</p>
            </div>
          </div>
        ) : quizzes.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="text-5xl">📭</div>
            <p className="text-white/60 text-sm">Aucun quiz partagé pour l'instant.</p>
            <button onClick={() => navigate('/home')} className="text-primary hover:underline text-sm">
              ← Retour à l'accueil
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {quizzes.map(({ deck_id, deck, played }) => (
              <div key={deck_id} className="relative bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                {/* Croix retrait */}
                <button
                  onClick={() => setConfirmRemove({ deckId: deck_id, deckTitle: deck?.titre ?? '' })}
                  className="absolute top-3 right-3 text-white/30 hover:text-white/70 hover:bg-white/10 rounded-lg p-1 transition"
                  title="Retirer ce quiz"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Infos deck */}
                <div className="pr-6">
                  <h2 className="text-white font-semibold text-base leading-snug line-clamp-2">{deck?.titre}</h2>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {deck?.education_level && deck.education_level !== 'Non applicable' && (
                      <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">{deck.education_level}</span>
                    )}
                    {deck?.matiere && (
                      <span className="bg-purple-500/20 text-purple-300 text-xs px-2 py-0.5 rounded-full">{deck.matiere}</span>
                    )}
                    {deck?.difficulty && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${DIFFICULTY_COLOR[deck.difficulty] ?? 'bg-white/10 text-white/60'}`}>
                        {deck.difficulty}
                      </span>
                    )}
                    {played && (
                      <span className="bg-green-500/20 text-green-300 text-xs px-2 py-0.5 rounded-full">✓ Déjà joué</span>
                    )}
                  </div>
                </div>

                {/* Bouton Jouer */}
                <button
                  onClick={() => setPlayDeck(deck ? { ...deck, id: deck_id } : null)}
                  className="mt-auto w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-xl text-sm transition active:scale-95"
                >
                  Jouer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      </main>

      {/* Modal confirmation retrait */}
      {confirmRemove && (
        <ConfirmRemoveModal
          deckTitle={confirmRemove.deckTitle}
          onConfirm={() => handleRemove(confirmRemove.deckId)}
          onCancel={() => setConfirmRemove(null)}
        />
      )}

      {/* Modal lancer solo */}
      {playDeck && session && (
        <PlaySoloModal
          deck={playDeck}
          session={session}
          onCancel={() => setPlayDeck(null)}
        />
      )}
    </div>
  )
}
